/** The notify module's HTTP API (apps/hub/src/ext/notify) and its live slice, state.ext.notify. */
import type {
  ChannelPatch,
  ChannelView,
  DailySummary,
  EmailChannelInput,
  NotifyInbox,
  NotifyMember,
  NotifyMemberInput,
  NotifySettings,
  NotifyState,
  NotifyView,
  OneTapPreview,
  OneTapResult,
  RotaInput,
  RotaView,
  SmtpInput,
  SmtpView,
  SubscribeRequest,
  TestSendResponse,
  WebhookChannelInput,
} from '@orgus/protocol';
import { api, apiDelete, apiGet, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

export const getNotify = (signal?: AbortSignal) => apiGet<NotifyView>('/api/notify', signal);
export const saveNotifySettings = (s: Partial<NotifySettings>) => api<NotifyView>('/api/notify/settings', { method: 'PUT', body: s });
export const subscribeDevice = (req: SubscribeRequest) => apiPost<ChannelView>('/api/notify/subscriptions', req);
export const unsubscribeDevice = (endpoint: string) => api<{ ok: true }>('/api/notify/subscriptions', { method: 'DELETE', body: { endpoint } });
export const addWebhook = (input: WebhookChannelInput) => apiPost<ChannelView>('/api/notify/channels', input);
export const addEmailChannel = (input: Omit<EmailChannelInput, 'kind'>) => apiPost<ChannelView>('/api/notify/channels', { ...input, kind: 'email' });
export const patchChannel = (id: string, patch: ChannelPatch) => api<ChannelView>(`/api/notify/channels/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
export const deleteChannel = (id: string) => apiDelete<{ ok: true }>(`/api/notify/channels/${encodeURIComponent(id)}`);
export const testSend = (channelId?: string) => apiPost<TestSendResponse>('/api/notify/test', channelId ? { channelId } : {});

export const saveSmtp = (input: SmtpInput) => api<SmtpView>('/api/notify/smtp', { method: 'PUT', body: input });
export const removeSmtp = () => apiDelete<SmtpView>('/api/notify/smtp');
export const testSmtp = (to: string) => apiPost<{ ok: true; smtp: SmtpView }>('/api/notify/smtp/test', { to });

export const getSummary = (signal?: AbortSignal) => apiGet<{ summary: DailySummary; text: string; line: string }>('/api/notify/summary', signal);
export const sendSummaryNow = () => apiPost<{ notification: unknown }>('/api/notify/digest');

export const addMember = (input: NotifyMemberInput) => apiPost<NotifyMember>('/api/notify/members', input);
export const patchMember = (id: string, input: NotifyMemberInput) => api<NotifyMember>(`/api/notify/members/${encodeURIComponent(id)}`, { method: 'PATCH', body: input });
export const deleteMember = (id: string) => apiDelete<{ ok: true }>(`/api/notify/members/${encodeURIComponent(id)}`);
export const importMembers = () => apiPost<{ added: number; members: NotifyMember[] }>('/api/notify/members/import');

export const addRota = (input: RotaInput) => apiPost<RotaView>('/api/notify/rotas', input);
export const patchRota = (id: string, input: RotaInput) => api<RotaView>(`/api/notify/rotas/${encodeURIComponent(id)}`, { method: 'PATCH', body: input });
export const deleteRota = (id: string) => apiDelete<{ ok: true }>(`/api/notify/rotas/${encodeURIComponent(id)}`);
export const ackEscalation = (key: string) => apiPost<{ ok: true; message: string }>(`/api/notify/escalations/${encodeURIComponent(key)}/ack`);

export const getInbox = (signal?: AbortSignal) => apiGet<NotifyInbox>('/api/notify/inbox', signal);
export const markSeen = (at: number) => apiPost<Pick<NotifyInbox, 'lastSeenAt'>>('/api/notify/seen', { at });

export const previewOneTap = (token: string, signal?: AbortSignal) => apiGet<OneTapPreview>(`/api/notify/act?t=${encodeURIComponent(token)}`, signal);
export const performOneTap = (token: string) => apiPost<OneTapResult>('/api/notify/act', { t: token });

function isNotifyState(v: unknown): v is NotifyState {
  return !!v && typeof v === 'object' && Array.isArray((v as NotifyState).channels) && Array.isArray((v as NotifyState).recent);
}

/** state.ext.notify, or undefined when the hub has no notify module. */
export const useNotifyState = (): NotifyState | undefined =>
  useOrgus((s) => {
    const v = s.ext?.notify;
    return isNotifyState(v) ? v : undefined;
  });
